import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { fetchUserSessions, fetchDiagnosticScores, fetchAIReport, fetchGeneratedPDF, getSignedDownloadUrl, generatePDF } from "@/services/diagnosticService";
import { toast } from "sonner";
import {
  ArrowRight, FileBarChart, Calendar,
  TrendingUp, Loader2, LogIn, Plus, Download, FileText, CheckCircle2
} from "lucide-react";
import DiagnosticResults from "@/components/diagnostic/DiagnosticResults";
import AuthModal from "@/components/diagnostic/AuthModal";

const statusLabels = {
  in_progress: { label: "En cours", color: "text-yellow-400 bg-yellow-400/10" },
  completed: { label: "Terminé", color: "text-[#59C3A6] bg-[#59C3A6]/10" },
  submitted: { label: "Soumis", color: "text-blue-400 bg-blue-400/10" },
};

export default function MonEspacePage() {
  const { user, loading: authLoading } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedScores, setSelectedScores] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pdfFiles, setPdfFiles] = useState({}); // { sessionId: { ...pdfFileData, cachedUrl?: string } }
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(null);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    loadSessions();
  }, [user]);

  const loadSessions = async () => {
    try {
      const data = await fetchUserSessions(user.id);
      setSessions(data || []);
      
      // Check for existing PDF files for completed sessions
      const completedSessions = (data || []).filter(s => s.status === 'completed' || s.status === 'submitted');
      const pdfPromises = completedSessions.map(async (session) => {
        try {
          const pdfFile = await fetchGeneratedPDF(session.id);
          return { sessionId: session.id, pdfFile };
        } catch {
          return { sessionId: session.id, pdfFile: null };
        }
      });
      
      const pdfResults = await Promise.all(pdfPromises);
      const pdfMap = {};
      pdfResults.forEach(({ sessionId, pdfFile }) => {
        if (pdfFile) pdfMap[sessionId] = pdfFile;
      });
      setPdfFiles(pdfMap);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Download existing PDF
  const handleDownloadPDF = async (session, e) => {
    e.stopPropagation();
    const pdfFile = pdfFiles[session.id];
    
    if (!pdfFile) {
      toast.error("Fichier PDF non trouvé");
      return;
    }

    // Utiliser la cachedUrl si disponible et non expirée
    if (pdfFile.cachedUrl && pdfFile.cachedUrlExpires && Date.now() < pdfFile.cachedUrlExpires) {
      window.open(pdfFile.cachedUrl, "_blank");
      toast.success("Téléchargement du PDF en cours");
      return;
    }

    // Sinon, générer une nouvelle signed URL
    if (!pdfFile.storage_path) {
      toast.error("Chemin du fichier PDF non trouvé");
      return;
    }

    setDownloadingPdf(session.id);
    try {
      const signedUrl = await getSignedDownloadUrl(pdfFile.storage_path);
      window.open(signedUrl, "_blank");
      toast.success("Téléchargement du PDF en cours");
      
      // Mettre en cache la nouvelle URL
      setPdfFiles(prev => ({
        ...prev,
        [session.id]: {
          ...prev[session.id],
          cachedUrl: signedUrl,
          cachedUrlExpires: Date.now() + 55 * 60 * 1000,
        }
      }));
    } catch (err) {
      toast.error(err.message || "Erreur lors du téléchargement");
    } finally {
      setDownloadingPdf(null);
    }
  };

  // Generate new PDF
  const handleGeneratePDF = async (session, e) => {
    e.stopPropagation();
    
    // Prevent double clicks
    if (generatingPdf === session.id) return;
    
    setGeneratingPdf(session.id);
    try {
      const data = await generatePDF(session.id);

      // Succès : generatePDF garantit qu'on a download_url si pas d'erreur
      // Utiliser directement la signed_url retournée
      window.open(data.download_url, "_blank");
      toast.success("Votre rapport PDF a été généré avec succès !", {
        description: "Le PDF s'ouvre dans un nouvel onglet",
        duration: 5000,
      });
      
      // Mettre à jour pdfFiles avec les données retournées directement
      // (évite une requête supplémentaire à generated_files)
      setPdfFiles(prev => ({
        ...prev,
        [session.id]: {
          id: data.id || session.id,
          file_name: data.file_name || "rapport-finoveo.pdf",
          storage_path: data.storage_path,
          file_type: "pdf_report",
          mime_type: "application/pdf",
          created_at: new Date().toISOString(),
          is_active: true,
          // Stocker la signed_url pour téléchargement immédiat (valide 1h)
          cachedUrl: data.download_url,
          cachedUrlExpires: Date.now() + 55 * 60 * 1000, // 55 min (marge de sécurité)
        }
      }));
      
      // Email notification (non-bloquant)
      if (data.email_status === "sent") {
        setTimeout(() => {
          toast.success("Un email avec le rapport vous a été envoyé", {
            icon: "📧",
            duration: 4000,
          });
        }, 1500);
      } else if (data._emailWarning || data.email_status === "failed") {
        // Warning silencieux - ne bloque pas le téléchargement
        setTimeout(() => {
          toast.info("L'email n'a pas pu être envoyé, mais votre PDF est disponible.", {
            duration: 4000,
          });
        }, 1500);
      }
    } catch (err) {
      toast.error("Une erreur est survenue lors de la génération du PDF", {
        description: err.message || "Veuillez réessayer",
      });
    } finally {
      setGeneratingPdf(null);
    }
  };

  // Format date helper
  const formatPdfDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
    });
  };

  const viewSession = async (session) => {
    setSelectedSession(session);
    setLoadingDetails(true);
    try {
      const [scores, report] = await Promise.all([
        fetchDiagnosticScores(session.id),
        fetchAIReport(session.id).catch(() => null),
      ]);
      setSelectedScores(scores);
      setSelectedReport(report);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingDetails(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B1F3B] pt-20">
        <Loader2 size={40} className="animate-spin text-[#59C3A6]" />
      </div>
    );
  }

  // Not logged in - redirect to login page
  if (!user) {
    return (
      <div data-testid="mon-espace-unauth" className="min-h-screen flex items-center justify-center bg-[#0B1F3B] pt-20 px-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-[#59C3A6]/20 flex items-center justify-center mx-auto mb-5">
            <LogIn size={28} className="text-[#59C3A6]" />
          </div>
          <h2 className="font-heading text-2xl font-semibold text-white mb-3">
            Connectez-vous pour accéder à votre espace
          </h2>
          <p className="text-slate-400 text-sm mb-6">
            Retrouvez vos diagnostics, scores et recommandations personnalisées.
          </p>
          <Link to="/connexion?redirect=/mon-espace">
            <Button
              data-testid="mon-espace-login-btn"
              className="bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold px-6 py-2.5 rounded-full"
            >
              Se connecter
            </Button>
          </Link>
          <p className="text-slate-500 text-sm mt-4">
            Pas encore de compte ?{" "}
            <Link to="/diagnostic" className="text-[#59C3A6] hover:underline">
              Faire mon diagnostic
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // Viewing a specific session's results
  if (selectedSession && !loadingDetails) {
    return (
      <div className="min-h-screen bg-[#0B1F3B] pt-28 pb-20 px-6 md:px-12 lg:px-16">
        <div className="max-w-4xl mx-auto">
          <button
            data-testid="back-to-sessions"
            onClick={() => { setSelectedSession(null); setSelectedScores(null); setSelectedReport(null); }}
            className="text-[#59C3A6] text-sm mb-6 hover:underline flex items-center gap-1"
          >
            &larr; Retour à mes diagnostics
          </button>
          {selectedScores && selectedReport ? (
            <DiagnosticResults scores={selectedScores} report={selectedReport} sessionId={selectedSession.id} />
          ) : (
            <div className="text-center py-12">
              <p className="text-slate-400">Les résultats ne sont pas encore disponibles pour cette session.</p>
              <p className="text-slate-500 text-sm mt-2">Statut : {statusLabels[selectedSession.status]?.label || selectedSession.status}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (loadingDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B1F3B] pt-20">
        <Loader2 size={40} className="animate-spin text-[#59C3A6]" />
      </div>
    );
  }

  // Sessions list
  return (
    <div data-testid="mon-espace-page" className="min-h-screen bg-[#0B1F3B] pt-28 pb-20 px-6 md:px-12 lg:px-16">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">Mon espace</h1>
            <p className="text-slate-400 text-base">Retrouvez tous vos diagnostics et recommandations.</p>
          </div>
          <Link to="/diagnostic">
            <Button
              data-testid="new-diagnostic-btn"
              className="bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold px-5 py-2.5 rounded-full"
            >
              <Plus size={16} className="mr-2" /> Nouveau diagnostic
            </Button>
          </Link>
        </div>

        {sessions.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16 rounded-2xl bg-[#112A4F]/30 border border-white/5"
          >
            <FileBarChart size={48} className="text-slate-600 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-white mb-2">Aucun diagnostic</h3>
            <p className="text-slate-400 text-sm mb-6">Commencez votre premier diagnostic pour analyser votre activité.</p>
            <Link to="/diagnostic">
              <Button className="bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold px-6 py-2.5 rounded-full">
                Démarrer un diagnostic <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {sessions.map((session, i) => {
              const status = statusLabels[session.status] || { label: session.status, color: "text-slate-400 bg-slate-400/10" };
              const pdfFile = pdfFiles[session.id];
              const hasPdf = !!pdfFile;
              const isCompleted = session.status === 'completed' || session.status === 'submitted';
              const isDownloading = downloadingPdf === session.id;
              const isGenerating = generatingPdf === session.id;
              const pdfDate = pdfFile?.created_at ? formatPdfDate(pdfFile.created_at) : null;
              
              return (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  data-testid={`session-card-${i}`}
                  className="p-6 rounded-2xl bg-[#112A4F]/30 border border-white/5 hover:border-[#59C3A6]/15 transition-all cursor-pointer group"
                  onClick={() => viewSession(session)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#59C3A6]/10 flex items-center justify-center relative">
                        <TrendingUp size={22} className="text-[#59C3A6]" />
                        {/* PDF indicator badge */}
                        {hasPdf && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#59C3A6] rounded-full flex items-center justify-center">
                            <FileText size={10} className="text-[#0B1F3B]" />
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="font-heading text-base font-semibold text-white flex items-center gap-2">
                          {session.companies?.company_name || "Diagnostic"}
                        </h3>
                        <div className="flex items-center gap-3 text-slate-500 text-xs mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {new Date(session.created_at).toLocaleDateString("fr-FR")}
                          </span>
                          {session.companies?.industry && (
                            <span>{session.companies.industry}</span>
                          )}
                          {hasPdf && pdfDate && (
                            <span className="flex items-center gap-1 text-[#59C3A6]">
                              <CheckCircle2 size={10} />
                              PDF {pdfDate}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {isCompleted && (
                        hasPdf ? (
                          <Button
                            data-testid={`download-pdf-${i}`}
                            onClick={(e) => handleDownloadPDF(session, e)}
                            disabled={isDownloading}
                            variant="outline"
                            size="sm"
                            className="border-[#59C3A6]/30 text-[#59C3A6] hover:bg-[#59C3A6]/10 rounded-full px-4 py-1.5 h-auto text-xs font-medium"
                          >
                            {isDownloading ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <>
                                <Download size={14} className="mr-1.5" />
                                Télécharger PDF
                              </>
                            )}
                          </Button>
                        ) : (
                          <Button
                            data-testid={`generate-pdf-${i}`}
                            onClick={(e) => handleGeneratePDF(session, e)}
                            disabled={isGenerating}
                            variant="outline"
                            size="sm"
                            className="border-slate-600 text-slate-400 hover:bg-slate-700/30 hover:text-slate-300 rounded-full px-4 py-1.5 h-auto text-xs font-medium"
                          >
                            {isGenerating ? (
                              <>
                                <Loader2 size={14} className="animate-spin mr-1.5" />
                                Génération...
                              </>
                            ) : (
                              <>
                                <FileText size={14} className="mr-1" />
                                Générer PDF
                              </>
                            )}
                          </Button>
                        )
                      )}
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>
                        {status.label}
                      </span>
                      <ArrowRight size={16} className="text-slate-500" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
